import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../api/supabase'
import { usePagination } from '../../hooks/usePagination'
import PaginationControls from '../../components/PaginationControls'
import { getCachedCount, setCachedCount, invalidateCachedCount } from '../../lib/countCache'
import { generateNaFile, downloadNaFileCsv, type NaRegistryRow } from '../../api/naFileExporter'

// Z73.18 Phase 1.6: /admin/na-registry -- list of parties/premises to register with Descartes'
// NA (Names & Addresses) master data, same Z35.1 shared pagination pattern as Audit Log /
// Jobs / Customers. Status badge reflects descartes_registered_at, set manually by Gary once
// he's uploaded a generated NA file to Descartes UAT and confirmed it landed (no live
// registration-status API from Descartes -- this app can generate the file, not read acceptance
// back automatically, so the badge is honest about being staff-confirmed, not system-verified).

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

interface Row extends NaRegistryRow {
  descartes_registered_at: string | null
  descartes_environment: string | null
  source: string
  created_at: string
}

function StatusBadge({ row }: { row: Row }) {
  if (row.descartes_registered_at) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
        🟢 Registered {row.descartes_environment ? `(${row.descartes_environment})` : ''}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
      ⚪ Pending
    </span>
  )
}

const emptyForm = {
  shortname: '', type: 'CDS', tid: '', longname: '', street: '', city: '', postcode: '', country: 'GB',
  vat_number: '', dan: '', rep_type_imports: '', rep_type_exports: '', rep_type_cfsp: '',
  guarantee_shortname: '', aeo: '', rex: '', seed: '', trader_excise: '',
}

function NewPartyModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    if (!form.shortname.trim() || !form.longname.trim() || !form.street.trim() || !form.city.trim() || !form.country.trim()) {
      setError('Shortname, longname, street, city and country are required.')
      return
    }
    setSaving(true); setError(null)
    const { error: err } = await supabase.from('na_registry').insert({
      ...form,
      tid: form.tid || null, postcode: form.postcode || null, vat_number: form.vat_number || null,
      dan: form.dan || null, rep_type_imports: form.rep_type_imports || null,
      rep_type_exports: form.rep_type_exports || null, rep_type_cfsp: form.rep_type_cfsp || null,
      guarantee_shortname: form.guarantee_shortname || null, aeo: form.aeo || null,
      rex: form.rex || null, seed: form.seed || null, trader_excise: form.trader_excise || null,
      source: 'manual',
    })
    setSaving(false)
    if (err) { setError(err.message); return }
    onSaved()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">New NA Registry Party</h2>
        {error && <div className="mb-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-3 py-2">{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-xs text-gray-500">Shortname (max 18) *
            <input className={inputCls} maxLength={18} value={form.shortname} onChange={e => setForm(f => ({ ...f, shortname: e.target.value.toUpperCase() }))} />
          </label>
          <label className="text-xs text-gray-500">Type
            <select className={inputCls} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              <option value="CDS">CDS</option>
              <option value="XX">XX</option>
              <option value="WH">WH (Warehouse)</option>
            </select>
          </label>
          <label className="text-xs text-gray-500">TID (EORI / Warehouse Code)
            <input className={inputCls} value={form.tid} onChange={e => setForm(f => ({ ...f, tid: e.target.value }))} />
          </label>
          <label className="col-span-2 text-xs text-gray-500">Long name *
            <input className={inputCls} maxLength={35} value={form.longname} onChange={e => setForm(f => ({ ...f, longname: e.target.value }))} />
          </label>
          <label className="col-span-2 text-xs text-gray-500">Street *
            <input className={inputCls} maxLength={35} value={form.street} onChange={e => setForm(f => ({ ...f, street: e.target.value }))} />
          </label>
          <label className="text-xs text-gray-500">City *
            <input className={inputCls} maxLength={35} value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          </label>
          <label className="text-xs text-gray-500">Postcode
            <input className={inputCls} maxLength={9} value={form.postcode} onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))} />
          </label>
          <label className="text-xs text-gray-500">Country (ISO2) *
            <input className={inputCls} maxLength={2} value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value.toUpperCase() }))} />
          </label>
          <label className="text-xs text-gray-500">VAT / TURN
            <input className={inputCls} value={form.vat_number} onChange={e => setForm(f => ({ ...f, vat_number: e.target.value }))} />
          </label>
          <label className="text-xs text-gray-500">DAN
            <input className={inputCls} value={form.dan} onChange={e => setForm(f => ({ ...f, dan: e.target.value }))} />
          </label>
          <label className="text-xs text-gray-500">Guarantee shortname
            <input className={inputCls} value={form.guarantee_shortname} onChange={e => setForm(f => ({ ...f, guarantee_shortname: e.target.value }))} />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function NaRegistry() {
  const [rows, setRows] = useState<Row[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const { page, setPage, pageSize, setPageSize, range } = usePagination('na-registry', 100)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filtersVersion, setFiltersVersion] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showNew, setShowNew] = useState(false)
  const [generating, setGenerating] = useState(false)

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const countCacheKey = `na-registry:${search}`
      const cachedCount = getCachedCount(countCacheKey)

      let q = supabase
        .from('na_registry')
        .select('*', cachedCount === undefined ? { count: 'exact' } : undefined)
        .order('shortname', { ascending: true })

      if (search) q = q.or(`shortname.ilike.%${search}%,longname.ilike.%${search}%,tid.ilike.%${search}%`)
      if (range) q = q.range(range[0], range[1])

      const { data, error: err, count } = await q
      if (err) throw err
      setRows((data as Row[]) || [])
      if (cachedCount !== undefined) {
        setTotal(cachedCount)
      } else {
        setTotal(count ?? 0)
        setCachedCount(countCacheKey, count ?? 0)
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load NA registry')
    } finally {
      setLoading(false)
    }
  }, [search, range])

  useEffect(() => { fetchRows() }, [page, pageSize, filtersVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)))
  }

  async function handleGenerate() {
    const chosen = rows.filter(r => selected.has(r.id))
    if (chosen.length === 0) return
    setGenerating(true)
    try {
      const csv = await generateNaFile(chosen)
      downloadNaFileCsv(`NA_${new Date().toISOString().slice(0, 10)}.csv`, csv)
    } catch (e: any) {
      setError(e.message || 'Failed to generate NA file')
    } finally {
      setGenerating(false)
    }
  }

  async function markRegistered(id: string) {
    await supabase.from('na_registry').update({
      descartes_registered_at: new Date().toISOString(),
      descartes_environment: 'UAT',
    }).eq('id', id)
    invalidateCachedCount('na-registry')
    fetchRows()
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">📋 Customs — NA Registry</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Parties and premises to register with Descartes' Names &amp; Addresses master data. Generate an NA file for the selected rows, upload it to Descartes UAT, then mark them registered.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white shrink-0">
          + New Party
        </button>
      </div>

      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-3 py-2">{error}</div>}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          className={`${inputCls} max-w-xs`}
          placeholder="Search shortname / name / TID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setPage(0); setFiltersVersion(v => v + 1) } }}
        />
        <button onClick={() => { setPage(0); setFiltersVersion(v => v + 1) }} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600">
          Search
        </button>
        <div className="flex-1" />
        <button
          onClick={handleGenerate}
          disabled={selected.size === 0 || generating}
          className="px-4 py-2 text-sm rounded-lg bg-orange-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {generating ? 'Generating…' : `Generate NA File (${selected.size})`}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
        <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/60">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleSelectAll} />
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Shortname</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Long name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">TID</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Country</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Source</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No parties found.</td></tr>
              ) : rows.map(row => (
                <tr key={row.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} /></td>
                  <td className="px-3 py-2 font-mono">{row.shortname}</td>
                  <td className="px-3 py-2">{row.longname}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.tid || '—'}</td>
                  <td className="px-3 py-2">{row.country}</td>
                  <td className="px-3 py-2"><StatusBadge row={row} /></td>
                  <td className="px-3 py-2 text-xs text-gray-400">{row.source}</td>
                  <td className="px-3 py-2 text-right">
                    {!row.descartes_registered_at && (
                      <button onClick={() => markRegistered(row.id)} className="text-xs text-blue-600 hover:underline">
                        Mark registered
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />
      </div>

      {showNew && (
        <NewPartyModal
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); invalidateCachedCount('na-registry'); setPage(0); setFiltersVersion(v => v + 1) }}
        />
      )}
    </div>
  )
}
